<template lang="pug">
.wrap
	template(v-if="!checkoutActive")
		.pricing-wrap
			.products-header
				h1.title-text {{ t('products.list.title') }}
				.feature-list
					.feature
						SingaIcon.green-check(icon="checkmark" size="medium")
						span.feature-text {{ t('products.features.catalog', { catalog }) }}
					.feature
						SingaIcon.green-check(icon="checkmark" size="medium")
						span.feature-text {{ t('products.features.unlimitedSinging') }}
					.feature
						SingaIcon.green-check(icon="checkmark" size="medium")
						span.feature-text {{ t('products.features.queue') }}
			RadioGroupRoot.pricing-rows(v-model="radioStateSingle" :default-value="ProductType.MONTHLY" :id="ProductType.MONTHLY" aria-label="View density")
				RadioGroupItem.pricing-row(:value="ProductType.MONTHLY" v-if="products?.monthly")
					.title-wrap
						.name
							label.product-name(:for="ProductType.MONTHLY") {{ t('products.premium.interval.monthly') }}
							span.product-tag.primary {{ t('products.premium.tag.popular') }}
						.product-desc
							span.amount(v-html="formattedCurrency(products.monthly.rawMonthlyAmount, products.monthly.currency)")
							span {{ t('products.premium.price.interval') }}
					.indicator
					SingaIcon.indicator-checked(icon="checkmark-circle" size="large")
				SingaSkeleton.product-skeleton(v-else type="Rectangle" :dimensions="{ width: '100%', height: '87px' }")
				RadioGroupItem.pricing-row(:value="ProductType.YEARLY" :id="ProductType.YEARLY" v-if="products?.yearly")
					.title-wrap
						.name
							label.product-name(:for="ProductType.YEARLY") {{ t('products.premium.interval.yearly') }}
							span.product-tag {{ t('products.premium.tag.annualSavings', { amount: '20' }) }}
						.product-desc
							span.amount(v-if="legacyPaymentsEnabled && products.yearly.amount" v-html="formattedCurrency(products.yearly.amount, products.yearly.currency)")
							span.amount(v-else-if="products.yearly.unit_amount" v-html="formattedCurrency(products.yearly.unit_amount, products.yearly.currency)")
							span {{ t('products.premium.price.yearlyInfo.1') }}
							span(v-html="formattedCurrency(products.yearly.rawMonthlyAmount, products.yearly.currency)")
							span {{ t('products.premium.price.yearlyInfo.2') }}
					.indicator
					SingaIcon.indicator-checked(icon="checkmark-circle" size="large")
				SingaSkeleton.product-skeleton(v-else type="Rectangle" :dimensions="{ width: '100%', height: '87px' }")
				RadioGroupItem.pricing-row(:value="ProductType.PASS" :id="ProductType.PASS" v-if="products?.pass")
					.title-wrap
						.name
							label.product-name(:for="ProductType.PASS") {{ t('products.premium.pass.title') }}
						.product-desc
							span.amount(v-if="legacyPaymentsEnabled" v-html="formattedCurrency(products.pass.amount, products.pass.currency)")
							span.amount(v-else v-html="formattedCurrency(products.pass.unit_amount, products.pass.currency)")
							span {{ t('products.premium.price.passInfo') }}
					.indicator
					SingaIcon.indicator-checked(icon="checkmark-circle" size="large")
				SingaSkeleton(v-else type="Rectangle" :dimensions="{ width: '100%', height: '87px' }")
			SingaButton.is-regular.is-primary.is-full-width.cta-button(@click="selectProduct()") {{ t('products.premium.CTA') }}
	.stripe-checkout#checkout(ref="stripeCheckout")
</template>

<script setup lang="ts">
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui'

const { t } = useI18n()

const config = useRuntimeConfig()
const catalog = config.public.catalogCount
const { formattedCurrency, getPricePlans, getPasses, getPlans, getPassPrices } = useProducts()
const { legacyPaymentsEnabled } = useUserStore()
const { checkoutPortal } = useStripe()

const geoStore = useGeoLocationStore()
const { countryCode } = storeToRefs(geoStore)
const checkoutActive = ref(false)
enum ProductType {
	MONTHLY = 'monthly',
	YEARLY = 'yearly',
	PASS = 'pass'
}
const radioStateSingle = ref<ProductType>(ProductType.MONTHLY)

const { refresh: pricePlanRefresh, data: pricePlans } = await useAsyncData('prices' + countryCode, async () => {
	return await getPricePlans()
}, { lazy: true })

const { refresh: plansRefresh, data: plans } = await useAsyncData('plans' + countryCode, async () => {
	return await getPlans()
}, { lazy: true })

const { refresh: passRefresh, data: pass } = await useAsyncData('pass' + countryCode, async () => {
	return await getPasses()
}, { lazy: true })

const { refresh: passPriceRefresh, data: pricePass } = await useAsyncData('pricesPass' + countryCode, async () => {
	return await getPassPrices()
}, { lazy: true })

watch(countryCode, (newVal, oldVal) => {
	if (newVal?.toLowerCase() !== oldVal?.toLowerCase()) {
		pricePlanRefresh()
		plansRefresh()
		passRefresh()
		passPriceRefresh()
	}
})

const products = computed(() => {
	if (legacyPaymentsEnabled) {
		return {
			monthly: plans.value?.monthly,
			yearly: plans.value?.yearly,
			pass: pass.value
		}
	} else {
		return {
			monthly: pricePlans.value?.monthly,
			yearly: pricePlans.value?.yearly,
			pass: pricePass.value
		}
	}
})

// Define the emit function for the 'checkout-active-change' event
const emit = defineEmits(['checkout-active-change'])
const selectProduct = () => {
	checkoutActive.value = true
	if (legacyPaymentsEnabled) {
		navigateTo('https://singa.com/choose-plan', { external: true })
	} else {
		if (radioStateSingle.value === ProductType.MONTHLY) {
			checkoutPortal(pricePlans.value?.monthly, 'sub')
		} else if (radioStateSingle.value === ProductType.YEARLY) {
			checkoutPortal(pricePlans.value?.yearly, 'sub')
		} else if (radioStateSingle.value === ProductType.PASS) {
			checkoutPortal(pricePass.value, 'pass')
		}
	}
}

const props = defineProps({
	preSelectedProduct: {
		type: Object,
		required: false
	}
})

// Emit event when checkoutActive changes
watch(checkoutActive, (newValue) => {
	emit('checkout-active-change', newValue)
})

onMounted(() => {
	nextTick(() => {
		if (props.preSelectedProduct) {
			checkoutActive.value = true
			checkoutPortal(props.preSelectedProduct, props.preSelectedProduct.type)
		}
	})
})
</script>

<style lang="sass" scoped>
.pricing-wrap
	max-width: 546px + ($spacing-40 * 2)
	margin: auto
	background: $color-grey-80
	padding: $spacing-16
	border-radius: $radius-default
	@media (min-width: $tablet)
		padding: $spacing-40
.pricing-rows
	display: flex
	flex-direction: column
.indicator-checked
	display: none
	position: relative
	left: 4px
	:deep(svg)
		color: $primary
		path
			width: 32px
			height: 32px
.pricing-row
	display: flex
	justify-content: space-between
	align-items: center
	background-color: transparent
	border-radius: $radius-default
	border: 1px solid
	border-color: $transparent-white-16
	outline: 0
	position: relative
	cursor: pointer
	@media (min-width: $tablet)
		padding: $spacing-16 $spacing-32
		&:hover
			background-color: $transparent-white-4
	&:not(:last-child)
		margin-bottom: $spacing-16
	&:after
		content: ''
		width: 100%
		height: 100%
		position: absolute
		top: 0
		left: 0
		background-color: rgba($primary, 0.1)
		opacity: 0
		transition: opacity 0.2s
		z-index: -1
	&[data-state="checked"]
		background-color: rgba($primary, 0.1)
		border-color: $primary
		&:after
			opacity: 1
		.indicator
			display: none
		.indicator-checked
			display: block
.title-wrap
	display: flex
	flex-direction: column
	align-items: flex-start
.name
	padding-bottom: 1px
.product-desc
	text-align: left
	span
		color: $color-grey-40
		@include font(basier, medium)
		@include fontSize(s)
.product-tag
	padding: $spacing-4 $spacing-16
	@include font(basier, medium)
	@include fontSize(2xs)
	color: black
	border-radius: $radius-round
	margin-left: $spacing-8
	&:not(.primary)
		background-color: $color-grey-70
		color: $color-grey-20
	&.primary
		background-color: $primary
.product-name
	@include font(basier, medium)
	@include fontSize(m)
.indicator
	border-radius: $radius-round
	border: 1px solid
	border-color: $transparent-white-24
	width: 24px
	height: 24px
	transition: border-color 200ms
	&:hover
		border-color: $transparent-white-48
.cta-button
	margin-top: $spacing-32
.feature-list
	display: flex
	align-items: center
	justify-content: space-around
.green-check
	color: $primary
	margin-right: $spacing-8
	width: 20px
	height: 20px
.feature
	display: flex
	align-items: center
.products-header
	@include fontSize(s)
	color: $color-grey-20
	text-align: center
	padding: 0 0 $spacing-32
.title-text
	@include font(basier, bold)
	@include fontSize(xl)
	color: $color-grey-10
	padding-bottom: $spacing-8
	@media (min-width: $tablet)
		@include fontSize(4xl)
.stripe-checkout
	// padding: $spacing-32
	background: $color-grey-80
	border-radius: $radius-default
	overflow: hidden
.product-skeleton
	margin-bottom: $spacing-16
</style>
